import { gql } from "@apollo/client"

const EOC_STUDENT_CONTENT_ITEM = `
contentData {
  type
  id
  contentId
  refId
  parentId #deprecating
  parentContentId
  parentRefId
  practiceTestType
  name
  shortName
  description
  totalSeconds
  hasPreview
  hasDetails
  snapVideosCount
  questionsCount
  microSnapCount
  standards
  resources
  meta
  grouping
}
planData {
  guid
  planSectionId
  selectedMicroSnapCount
  isTeachLive
  isComplete
  breakBefore
  breakAfter
}
studentData {
  contentStatus
  scoreStatus
  blockedBy
  blockedById
  canStart
  canRestart
  canRestore
  completedAt
  completedScore
  percentCorrect
  assessmentId
  gradeLevels {
    name
    score
  }
}
`

export const GET_SKILL_REPORT_CONTENT_TARGETS = gql`
  query getSkillReportContentTargets($program: Program!, $refScope: refScope!) {
    getSkillReportContentTargets(program: $program, refScope: $refScope) {
      availableSubjects {
        name
        available
        contentTargets {
          id
          name
          refId
          parentContentId
          parentRefId
        }
      }
    }
  }
`

export const GET_STUDENT_COMPLETED_CONTENT_GRAPH = gql`
  query getStudentCompletedContentGraph(
    $data: StudentCompletedContentGraphFilterInput!
  ) {
    getStudentCompletedContentGraph(data: $data) {
      items {
        id
        name
        contentType
        hasChildren
        parentNodeId
        program
        subject
        test
        meta {
          level
        }
      }
      meta {
        page
        pageSize
        totalCount
      }
    }
  }
`

export const GET_STUDENT_COMPLETED_CONTENT_LIST = gql`
  query getStudentCompletedContentList(
    $data: StudentCompletedContentListFilterInput!
  ) {
    getStudentCompletedContentList(data: $data) {
      items {
        id
        name
        contentType
        hasChildren
        parentNodeId
        program
        subject
        test
        meta {
          level
        }
      }
      meta {
        page
        pageSize
        totalCount
      }
    }
  }
`

export const GET_SKILLS_LIST = gql`
  query getSkillsList($data: SkillsRequest!) {
    skills {
      getSkillsList(data: $data) {
        id
        name
        hasChildren
        sortIndex
        parentNodeIds
      }
    }
  }
`

export const GET_STUDENT_SKILLS_OVERVIEW = gql`
  query getStudentSkillsOverview(
    $program: Program!
    $refScope: refScope!
    $contentIds: [String!]
  ) {
    getStudentSkillsOverview(
      program: $program
      refScope: $refScope
      contentIds: $contentIds
    ) {
      subject
      percentCorrect
      totalQuestionsAnswered
      totalQuestionsCorrect
    }
  }
`

export const GET_SKILL_REPORT_BY_PROGRAM_COURSE_REF_SCOPE = gql`
  query getSkillReportByProgramCourseRefScope(
    $data: SkillReportByProgramCourseRefScopeRequest!
  ) {
    getSkillReportByProgramCourseRefScope(data: $data) {
      id
      type
      summaryValue
      gradeLevels {
        name
        score
      }
      skillEntries {
        id
        name
        proficiency
        hasDescription
        desc
        subEntries {
          id
          name
          proficiency
          hasDescription
          desc
          subEntries {
            id
            name
            proficiency
            hasDescription
            desc
            subEntries {
              id
              name
              proficiency
              hasDescription
              desc
              subEntries {
                id
                name
                proficiency
                hasDescription
                desc
              }
            }
          }
        }
      }
    }
  }
`

export const GET_SKILL_REPORT_BY_CONTENT_ID = gql`
  query getSkillReportByContentId(
    $program: Program!
    $subject: String!
    $refScope: refScope!
    $contentId: String!
    $parentSkillId: String
  ) {
    getSkillReportByContentId(
      program: $program
      subject: $subject
      refScope: $refScope
      contentId: $contentId
      parentSkillId: $parentSkillId
    ) {
      id
      type
      summaryValue
      gradeLevels {
        name
        score
      }
      skillEntries {
        id
        name
        proficiency
        hasDescription
        desc
        subEntries {
          id
          name
          proficiency
          hasDescription
          desc
          subEntries {
            id
            name
            proficiency
            hasDescription
            desc
            subEntries {
              id
              name
              proficiency
              hasDescription
              desc
              subEntries {
                id
                name
                proficiency
                hasDescription
                desc
              }
            }
          }
        }
      }
    }
  }
`

export const GET_CONTENT_DESCRIPTION = gql`
  query getContentDescription(
    $program: Program!
    $subject: String!
    $target: String!
    $refScope: refScope!
  ) {
    getContentDescription(
      program: $program
      subject: $subject
      target: $target
      refScope: $refScope
    )
  }
`

export const GET_ACTIVITY_REPORT_BY_REFSCOPE_PROGRAM_SUBJECT = gql`
  query getActivityReportByRefscopeProgramSubject(
    $program: Program!
    $subject: String!
    $refScope: refScope!
  ) {
    getActivityReportByRefscopeProgramSubject(
      program: $program
      subject: $subject
      refScope: $refScope
    ) {
      summaryValue
      gradeLevels {
        name
        score
      }
      categorizedTypeReports {
        name
        reports {
          id
          contentId
          name
          type
          percentCorrect
          percentComplete
          parentId #deprecating
          parentContentId
          parentRefId
          mostRecentCompletion
          scoreStatus
          rawScore
          gradeLevels {
            name
            score
          }
          refId
          # course ## not populated for this API
        }
      }
    }
  }
`

export const GET_COMBINED_ACTIVITY_REPORT_BY_REFSCOPE_PROGRAM = gql`
  query getCombinedActivityReportByRefscopeProgram(
    $program: Program!
    $refScope: refScope!
  ) {
    getCombinedActivityReportByRefscopeProgram(
      program: $program
      refScope: $refScope
    ) {
      summaryValue
      gradeLevels {
        name
        score
      }
      categorizedTypeReports {
        name
        reports {
          id
          contentId
          name
          type
          percentCorrect
          percentComplete
          parentId #deprecating
          parentContentId
          parentRefId
          mostRecentCompletion
          scoreStatus
          rawScore
          gradeLevels {
            name
            score
          }
          refId
          course
        }
      }
    }
  }
`

export const GET_COMBINED_SCORE_BY_REFSCOPE_PROGRAM = gql`
  query getCombinedScoreByRefscopeProgram(
    $program: Program!
    $refScope: refScope!
    $contentId: String # optional
  ) {
    getCombinedScoreByRefscopeProgram(
      program: $program
      contentId: $contentId
      refScope: $refScope
    ) {
      combinedScore
      gradeLevels {
        name
        score
      }
      courseScores {
        name
        score
        gradeLevels {
          name
          score
        }
      }
    }
  }
`

export const GET_INSIGHTS_BY_PROGRAM_REFSCOPE = gql`
  query getInsightsByProgramRefScope($program: Program!, $refScope: refScope!) {
    getInsightsByProgramRefScope(program: $program, refScope: $refScope) {
      subjectReports {
        subject
        numberComplete
        numberTotal
        percentComplete
        contentId
        projectedLevel
        gradeLevels {
          name
          score
        }
        combinedScore
      }
    }
  }
`

export const GET_EOC_STUDENT_SUMMARY = gql`
  query getEocStudentSummary($program: Program!) {
    getEocStudentSummary(program: $program) {
      programData {
        programStatus
        practiceTestCount
        masteryLessonCount
      }
      subjects {
        isPlanBuilt
        level
        subject
        subjectStatus
        percentageComplete
        recommendedSequence
        recommendedContent { ${EOC_STUDENT_CONTENT_ITEM} }
        recommendedContentAssignments {
          id
          due
          name
          status
          contentId
        }
      }
    }
  }
`

export const GET_ACCURACY_OVERTIME = gql`
  query AccuracyOverTime($data: StudentInsightsDashboardRequest!) {
    getInsightsAccuracyOverTime(data: $data) {
      points {
        accuracy
        time
      }
    }
  }
`

export const GET_INSIGHTS_SCORE_BY_PRACTICE_TEST = gql`
  query InsightsScoreByPracticeTest($data: StudentInsightsDashboardRequest!) {
    getInsightsScoreByPracticeTest(data: $data) {
      scores {
        practiceTestName
        practiceTestId
        score
      }
    }
  }
`

export const GET_INSIGHTS_DASHBOARD_METRICS = gql`
  query InsightsDashboardMetrics($data: StudentInsightsDashboardRequest!) {
    getInsightsDashboardMetrics(data: $data) {
      completionsCount
      correctAnswersPercentage
      questionsAnsweredCount
    }
  }
`

export const GET_INSIGHTS_SCORE_BY_PRACTICE_TEST_DETAILS = gql`
  query InsightsScoreByPracticeTestDetails(
    $data: StudentInsightsScoreByPracticeTestDetailsRequest!
  ) {
    getInsightsScoreByPracticeTestDetails(data: $data) {
      correctQuestions
      totalQuestions
      score
      contentId
      name
      parentId
    }
  }
`

export const GET_EOC_STUDENT_ITEMS = gql`
  query getEocStudentItems($program: Program!, $subject: String!) {
    getEocStudentItems(program: $program, subject: $subject) {
      canDoOutOfOrder
      subjectStatus
      isPlanBuilt
      contentItems { ${EOC_STUDENT_CONTENT_ITEM} }
    }
  }
`

export const GET_EOC_STUDENT_RECOMMENDATION = gql`
  query getEocStudentRecommendation($program: Program!, $subject: String!) {
    getEocStudentRecommendation(
      program: $program
      subject: $subject
    ) {
      subjectStatus
      recommendedContent { ${EOC_STUDENT_CONTENT_ITEM} }
    }
  }
`

export const GET_STUDENT_CONTENT_LIBRARY = gql`
  query getStudentContentLibrary($data: StudentContentLibraryInput!) {
    getStudentContentLibrary(data: $data) {
      content {
        id
        name
        description
        level
        entryType
        status
        completed
        score
        skills {
          name
        }
        percentage
        assessmentId
        userAssessmentId
        testType
      }
    }
  }
`

////
export const GET_AVAILABLE_COURSES_BY_PROGRAM = gql`
  query getAvailableCoursesByProgram($data: GetCoursesByProgramInput!) {
    getAvailableCoursesByProgram(data: $data) {
      name
      courseCategories
      labelTags
      properties {
        practiceTestType
        practiceTestHasSubtests
      }
    }
  }
`

export const GET_AVAILABLE_PROGRAMCOURSES = gql`
  query getAvailableProgramCourses($data: GetAvailableProgramsInput) {
    getAvailableProgramCourses(data: $data) {
      program
      programInfo {
        useGradelevels
        supportStudyPlan
        gradeLevelNames
        canTeachLive
        useDiagPracticeTest
        useTestTimingSetting
        defaultTestTimingValue
        displayName
        testType
      }
      courses {
        name
        courseCategories
        labelTags
        properties {
          practiceTestType
          practiceTestHasSubtests
        }
      }
    }
  }
`

export const GET_AVAILABLE_PROGRAMS = gql`
  query getAvailablePrograms($data: GetAvailableProgramsInput) {
    getAvailablePrograms(data: $data) {
      program
      programInfo {
        useGradelevels
        supportStudyPlan
        canTeachLive
        useDiagPracticeTest
        useTestTimingSetting
        defaultTestTimingPercentage
      }
    }
  }
`

export const GET_AVAILABLE_CONTENT_DURATION_BY_PROGRAM_COURSE = gql`
  query getAvailableContentDurationByProgramCourse($data: ProgramCourseInput) {
    getAvailableContentDurationByProgramCourse(data: $data)
  }
`

export const GET_STUDENT_TEST_SETTINGS_BY_PROGRAM_COURSE = gql`
  query getStudentTestSettingsByProgramCourse($data: ProgramCourseInput) {
    getStudentTestSettingsByProgramCourse(data: $data) {
      studentCanPauseTests
      studentTestTimingValue
      studentTestTimingType
    }
  }
`

export const GET_DOWNLOAD_LINK_PER_STUDENT_SKILL_REPORT_BY_CONTENT_ID = gql`
  query downloadPerStudentSkillReportByContentId(
    $program: Program!
    $subject: String!
    $refScope: refScope!
    $contentId: String!
  ) {
    downloadPerStudentSkillReportByContentId(
      program: $program
      subject: $subject
      refScope: $refScope
      contentId: $contentId
    ) {
      status
      url
    }
  }
`

export const GET_PER_STUDENT_SKILL_REPORT_BY_CONTENT_ID = gql`
  query getPerStudentSkillReportByContentId(
    $program: Program!
    $subject: String!
    $refScope: refScope!
    $contentId: String!
    $page: Int
    $pageSize: Int
  ) {
    getPerStudentSkillReportByContentId(
      program: $program
      subject: $subject
      refScope: $refScope
      contentId: $contentId
      page: $page
      pageSize: $pageSize
    ) {
      meta {
        page
        pageSize
        count
        totalCount
        hasNextPage
        hasPrevPage
      }
      columns {
        name
        type
      }
      rows
    }
  }
`

export const GET_USER_LICENSED_PROGRAMS_AND_PRODUCTS = gql`
  query getUserLicensedProgramsAndProducts(
    $data: GetUserLicensedProgramsAndProductsInput!
  ) {
    getUserLicensedProgramsAndProducts(data: $data) {
      program
      category
      programInfo {
        useGradelevels
        supportStudyPlan
        canTeachLive
        useDiagPracticeTest
        useTestTimingSetting
        defaultTestTimingValue
        displayName
      }
      products
    }
  }
`

export const GET_STUDENT_INSIGHTS_COMPLETIONS = gql`
  query GetStudentInsightsCompletions($data: StudentInsightsCompletedRequest!) {
    getStudentInsightsCompleted(data: $data) {
      answersCorrectRatio
      completedAt
      program
      rawScore
      scaledScore
      subject
      testId
      assessmentSessionSectionId
      assessmentId
      setId
      contentRegistryId
      testName
      type
    }
  }
`

export const GET_STUDENT_ASSIGNMENTS = gql`
  query GetStudentAssignments($data: GetStudentAssignmentsListRequest!) {
    assignments {
      getStudentList(data: $data) {
        assignments {
          id
          name
          status
          contentType
          contentId
          open
          close
          due
          settings
          canStart
          districtId
          schoolId
          updated_by
          updated_at
          created_by
          created_at
          completionStatus
          completedAt
          program
          subject
          assignees {
            type
            id
            name
          }
          percentComplete
          averageScore
        }
        pageCount
      }
    }
  }
`

export const GET_STUDENT_PERFORMANCE_REPORT = gql`
  query GetStudentPerformanceReport(
    $contentIds: [String!]!
    $refScope: refScope!
  ) {
    getStudentPerformanceReport(contentIds: $contentIds, refScope: $refScope) {
      reports {
        program
        reports {
          contentId
          program
          studentName
          testName
          overallScore {
            score
            isAvailable
          }
          subtests {
            subtestContentId
            subtestName
            status
            score
            questionsCount
            correctQuestionsCount
            completedDate
            accuracyPercent
            maxScore
            minScore
            items {
              itemNumber
              answer
              correctAnswer
              isCorrect
              skill
            }
          }
        }
      }
    }
  }
`

export const GET_LIST_STUDENT_PERFORMANCE_REPORTS = gql`
  query ListStudentPerformanceReports(
    $refScope: refScope!
    $sort: PropertySort
  ) {
    listStudentPerformanceReports(refScope: $refScope, sort: $sort) {
      reports {
        contentId
        name
        program
        lastActivity
        programDisplayName
      }
    }
  }
`
